// CL.4 C — CFDI al paciente («Facturas a pacientes»), lado del panel.
//
// 🔴 TODO OCULTO CON LA BANDERA APAGADA. El backend responde 404 a todas las rutas de CFDI
//    cuando `CFDI_PACIENTES` está vacía; aquí un 404 (o un 403: sin la capacidad `cfdi`) se
//    traduce en «no hay nada que pintar» y cada pantalla se queda EXACTAMENTE como hoy.
// 🔴 EL FRONTEND NO DECIDE NADA FISCAL: modo test/live, cupo, claves SAT resueltas, forma de
//    pago y receptor los calcula el servidor (vista previa) y aquí sólo se pintan.
// 🔴 COPY: los textos de este archivo — # Copy aprobado por Jesús (4 Oct 2026) — y los de
//    `T_HIST` (CL.4 C3): Copy aprobado por Jesús (5 Oct 2026). Se listan en IA/reportes/CL/cl4c.md
//    y en IA/reportes/CL/cl4c3-historial.md. Donde había un texto ya aprobado
//    (etiquetas de régimen y uso de CFDI de Facturación, «Facturar», «Descargar PDF»…) se reusa.
import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "./api";

export const MANIFIESTO_URL = "https://www.facturapi.io/embedded/manifiesto";

// Copy aprobado por Jesús (18 Oct 2026)
export const T_PAQUETE = {
  comprar: (cantidad) => `Comprar ${cantidad} facturas extra`,
  confirmar: (cantidad, precio) => `${cantidad} facturas extra por ${precio}. Es un solo pago. Las facturas extra son para este mes: vencen al terminar el mes y no pasan al siguiente. ¿Continuar al pago?`,
  abriendo: "Abriendo el pago…",
  error: "No pudimos abrir el pago. Inténtalo de nuevo.",
  retorno: "Estamos confirmando tu pago. Las facturas extra aparecerán en tu cupo en cuanto se acrediten.",
  cancelado: "Saliste del pago sin completarlo.",
};

export async function checkoutPaqueteCfdi() {
  const { data } = await api.post("/cfdi/paquetes/checkout");
  if (typeof data?.checkout_url !== "string" || !data.checkout_url.startsWith("https://")) throw new Error();
  window.location.assign(data.checkout_url);
}

// Sondeo corto del estado, sin modificar el cupo ni inferir una acreditación del retorno.
export function useRetornoPaqueteCfdi(onCambio) {
  const recargar = useRef(onCambio);
  const vuelta = useRef(new URLSearchParams(window.location.search).get("paquete_cfdi"));
  recargar.current = onCambio;
  const [aviso, setAviso] = useState(null);
  useEffect(() => {
    const url = new URL(window.location.href);
    const retorno = vuelta.current;
    if (!["ok", "cancelado"].includes(retorno)) return undefined;
    url.searchParams.delete("paquete_cfdi");
    window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
    setAviso(retorno === "ok" ? T_PAQUETE.retorno : T_PAQUETE.cancelado);
    if (retorno !== "ok") return undefined;
    let vivo = true;
    let timer;
    let intentos = 0;
    const consultar = async () => {
      try { await recargar.current?.(); } catch { /* El panel conserva el error de consulta. */ }
      intentos += 1;
      if (vivo && intentos < 4) timer = setTimeout(consultar, 1500);
    };
    consultar();
    return () => { vivo = false; clearTimeout(timer); };
  }, []);
  return aviso;
}

// Copy aprobado por Jesús (18 Oct 2026)
export const T_GLOBAL = {
  titulo: "Factura global",
  mes: "Mes cerrado",
  operaciones: (n) => `${n} operaciones`,
  quitar: "Quitar operación para facturarla a nombre del paciente",
  confirmar: "Se cancelará la global y se reexpedirá sin esta operación.",
  reexpedicion: "Reexpedición",
  errores: {
    GLOBAL_MES_ABIERTO: "Elige un mes cerrado.",
    GLOBAL_SIN_OPERACIONES: "No hay operaciones para esta factura global.",
    COBRO_EN_FACTURA_GLOBAL: "Este cobro está en una factura global. Quita la operación desde esa factura para facturarla a nombre del paciente.",
    GLOBAL_SIN_ALCANCE: "No tienes acceso a todas las operaciones de este emisor.",
  },
};

// Copy aprobado por Jesús (17 Oct 2026)
export const T_EMISORES = {
  emisor: "Emisor",
  clinica: "Clínica",
  medico: (nombre) => /^(Dr|Dra)\.?\s/i.test(nombre) ? nombre : `Dr. ${nombre}`,
  alta: "Dar de alta emisor de un médico",
  elegir: "Selecciona un médico",
  datos: "Datos fiscales del médico",
};

export function rutaEmisor(sufijo = "", doctorId = "") {
  return `/cfdi-pacientes/emisor${sufijo}${doctorId ? `?doctor_id=${encodeURIComponent(doctorId)}` : ""}`;
}

// Copy aprobado por Jesús (4 Oct 2026), y el bloque de Manifiesto (15 Oct 2026).
export const T = {
  tab: "Facturas a pacientes",
  modoPrueba: "Modo de prueba: las facturas no tienen validez fiscal.",
  modoReal: "Facturas con validez fiscal.",
  emisorTitulo: "Datos fiscales de la clínica",
  emisorSub: "Con estos datos se emiten las facturas a tus pacientes. El RFC se toma de tu certificado (CSD).",
  razonSocial: "Razón social (nombre como aparece en tu constancia)",
  regimen: "Régimen fiscal",
  cp: "Código postal fiscal",
  claveOmision: "Clave SAT por omisión (para conceptos sin clave)",
  ivaOmision: "IVA por omisión",
  guardar: "Guardar",
  guardando: "Guardando…",
  csdTitulo: "Certificado de sello digital (CSD)",
  csdSub: "Sube el archivo .cer, el archivo .key y su contraseña. No los guardamos: se envían al servicio de facturación.",
  csdCer: "Archivo .cer",
  csdKey: "Archivo .key",
  csdPassword: "Contraseña del CSD",
  csdSubir: "Subir certificado",
  csdVigente: (rfc, vence) => `RFC ${rfc} · vence el ${vence}`,
  csdSinCargar: "Sin certificado cargado.",
  cupo: (usados, maximo) => (maximo < 0 ? `${usados} facturas este mes` : `${usados} de ${maximo} facturas este mes`),
  cupoNoDisponible: "No pudimos consultar el cupo de tu plan.",
  pendientes: "Antes de facturar con validez fiscal falta completar en el servicio de facturación:",
  pasoManifiesto: "Firmar la Carta Manifiesto con la e.firma (FIEL)",
  // Copy aprobado por Jesús (15 Oct 2026)
  manifiestoTitulo: "Firma tu Carta Manifiesto",
  manifiestoCuerpo: (rfc) => `El SAT pide que autorices a nuestro proveedor de facturación a timbrar tus facturas. Se firma una sola vez con la e.firma (FIEL)${rfc ? ` del RFC ${rfc}` : ""}: tus archivos .cer y .key y su contraseña. No es el sello digital (CSD). Tu e.firma no se guarda.`,
  manifiestoFirmar: "Firmar con mi e.firma",
  manifiestoIframe: "Firma de la Carta Manifiesto",
  manifiestoAbrir: "Abrir en otra pestaña",
  manifiestoRevisar: "Ya firmé, revisar",
  manifiestoListo: "Listo. Ya puedes activar la facturación con validez fiscal.",
  manifiestoPendiente: "Aún no vemos la firma. Si ya firmaste, espera unos minutos y vuelve a revisar.",
  activarLive: "Activar facturas con validez fiscal",
  serviciosTitulo: "Clave SAT e IVA de tus servicios",
  serviciosSub: "Se usan al facturar cada concepto. Confírmalos con tu contador.",
  servicio: "Servicio",
  claveSat: "Clave SAT",
  unidad: "Unidad",
  iva: "IVA",
  facturar: "Facturar",
  facturaTitulo: "Facturar cobro",
  receptor: "¿A quién se factura?",
  seFacturaA: "Se factura a:",
  receptorPaciente: "Al paciente, con sus datos fiscales",
  receptorPublico: "Público en general (RFC genérico)",
  verVistaPrevia: "Ver vista previa",
  emitir: "Emitir factura",
  emitiendo: "Emitiendo…",
  enviarCorreo: "Enviar la factura por correo al paciente",
  correo: "Correo (opcional)",
  total: "Total",
  formaPago: "Forma de pago",
  usoCfdi: "Uso del CFDI",
  conceptos: "Conceptos",
  facturada: "Factura emitida.",
  timbrando: "El servicio de facturación está terminando de timbrar esta factura. Actualiza el estado en unos minutos.",
  reservada: "No supimos si la factura se emitió. Actualiza el estado antes de volver a intentarlo.",
  actualizar: "Actualizar estado",
  enviar: "Enviar por correo",
  cancelar: "Cancelar factura",
  cancelarTitulo: "Cancelar factura ante el SAT",
  cancelarAviso: "La cancelación no se puede deshacer.",
  motivo: "Motivo",
  sustituta: "Factura que la sustituye",
  confirmarCancelacion: "Cancelar factura",
  volver: "Volver",
  fiscalTitulo: "Datos fiscales del paciente",
  fiscalSub: "Para emitirle factura a su nombre.",
  rfc: "RFC",
  razonSocialPaciente: "Nombre o razón social (como en su constancia fiscal)",
  emailFactura: "Correo para recibir la factura",
  fiscalVacio: "Sin datos fiscales.",
  fiscalGuardado: "Datos fiscales guardados",
  estados: {
    reservado: "Pendiente de confirmar",
    timbrando: "Timbrando",
    vigente: "Vigente",
    error: "Con error",
    cancelacion_solicitada: "Cancelación en proceso",
    cancelado: "Cancelada",
  },
  enviada: "Factura enviada por correo",
  guardado: "Guardado",
  prueba: "(prueba)",
};

// CL.4 C3 — historial de facturas (pestaña «Facturas a pacientes» y ficha del paciente).
// Copy aprobado por Jesús (5 Oct 2026): todos los textos de este objeto (nuevos en C3, listados en
//    IA/reportes/CL/cl4c3-historial.md). Lo demás del historial reutiliza textos ya aprobados:
//    T.estados, T.prueba, T.cancelar…, «Paciente» y «Estado» (lib/anticipos.js), «Desde» y
//    «Hasta» (pages/Gastos.js), «Todos» (pages/CRM.js), «PDF» y «XML».
export const T_HIST = {
  titulo: "Historial de facturas", // Copy aprobado por Jesús (5 Oct 2026)
  buscarPh: "Nombre o RFC", // Copy aprobado por Jesús (5 Oct 2026)
  vacio: "No hay facturas con estos filtros.", // Copy aprobado por Jesús (5 Oct 2026)
  rango: (a, b, n) => `${a}–${b} de ${n}`, // Copy aprobado por Jesús (5 Oct 2026)
  anterior: "Anterior", // Copy aprobado por Jesús (5 Oct 2026)
  siguiente: "Siguiente", // Copy aprobado por Jesús (5 Oct 2026)
  pacienteTitulo: "Facturas del paciente", // Copy aprobado por Jesús (5 Oct 2026)
  pacienteVacio: "Este paciente no tiene facturas.", // Copy aprobado por Jesús (5 Oct 2026)
};
// Textos ya aprobados que el historial reutiliza tal cual (no son nuevos).
export const T_REUSO = {
  paciente: "Paciente", // = lib/anticipos.js rep_col_paciente (Copy aprobado por Jesús, 2 Oct 2026)
  estado: "Estado", // = lib/anticipos.js rep_col_estado (Copy aprobado por Jesús, 2 Oct 2026)
  desde: "Desde", // = pages/Gastos.js
  hasta: "Hasta", // = pages/Gastos.js
  todos: "Todos", // = pages/CRM.js (filtro de estado)
};

// Etiquetas de régimen y uso: las de Facturación (CL.3, ya aprobadas) donde existen.
// Las marcadas «nuevo» no existían en el panel (# Copy aprobado por Jesús (4 Oct 2026)).
export const REGIMENES_EMISOR = [
  { code: "612", label: "612 — Personas Físicas con Actividades Empresariales y Profesionales" },
  { code: "621", label: "621 — Incorporación Fiscal" },
  { code: "625", label: "625 — Régimen de las Actividades Empresariales con ingresos a través de Plataformas" },
  { code: "626", label: "626 — Régimen Simplificado de Confianza (RESICO)" },
];
export const REGIMENES_RECEPTOR = [
  { code: "605", label: "605 — Sueldos y Salarios e Ingresos Asimilados a Salarios" },   // nuevo
  { code: "606", label: "606 — Arrendamiento" },
  { code: "608", label: "608 — Demás ingresos" },                                          // nuevo
  { code: "611", label: "611 — Ingresos por Dividendos (socios y accionistas)" },          // nuevo
  { code: "612", label: "612 — Personas Físicas con Actividades Empresariales y Profesionales" },
  { code: "614", label: "614 — Ingresos por intereses" },                                  // nuevo
  { code: "616", label: "616 — Sin obligaciones fiscales" },
  { code: "621", label: "621 — Incorporación Fiscal" },
  { code: "625", label: "625 — Régimen de las Actividades Empresariales con ingresos a través de Plataformas" },
  { code: "626", label: "626 — Régimen Simplificado de Confianza (RESICO)" },
];
export const USOS = [
  { code: "D01", label: "D01 — Honorarios médicos y dentales" },
  { code: "D02", label: "D02 — Gastos médicos por incapacidad o discapacidad" },
  { code: "G03", label: "G03 — Gastos en general" },
  { code: "S01", label: "S01 — Sin efectos fiscales" },
];
export const IVAS = [
  { code: "exento", label: "Exento" },   // nuevo
  { code: "16", label: "16 %" },         // nuevo
  { code: "0", label: "0 %" },           // nuevo
];
// Motivos que ofrece la pantalla. El 01 (con sustituta) existe en la API, pero en v1 un cobro
// sólo admite UNA factura viva (índice cfdis_ingreso_vivo), así que la sustituta del MISMO cobro
// no se puede emitir antes de cancelar: se ofrece 02 y se vuelve a facturar. Ver informe CL.4 C.
export const MOTIVOS = [
  { code: "02", label: "02 — Comprobante emitido con errores sin relación" },    // nuevo
  { code: "03", label: "03 — No se llevó a cabo la operación" },                 // nuevo
];

export function datosDelError(e) {
  const d = e?.response?.data || {};
  return { detail: T_GLOBAL.errores[d.code] || formatApiError(d.detail), code: d.code || null, status: e?.response?.status || null, extra: d };
}

// ── ¿Está disponible para ESTA sesión? Una sola petición por clínica, compartida. ──
let cache = { clave: null, promesa: null };

export function invalidarEstadoCfdi() {
  cache = { clave: null, promesa: null };
}

export function cargarEstadoCfdi(claveSesion) {
  const clave = claveSesion || "_";
  if (cache.clave === clave && cache.promesa) return cache.promesa;
  cache = {
    clave,
    promesa: api.get("/cfdi-pacientes/emisor/estado").then(
      // Sólo cuenta como disponible la respuesta REAL del estado (`habilitado: true`).
      (r) => (r?.data?.habilitado === true
        ? { disponible: true, estado: r.data, error: null }
        : { disponible: false, estado: null, error: null }),
      (e) => {
        const st = e?.response?.status;
        // 404 = bandera apagada para esta clínica; 403 = sin la capacidad. En los dos casos no
        // se pinta NADA. Otro error (503 mal configurada / sin migración) sí se dice a quien
        // configura, pero no habilita facturar.
        if (st === 404 || st === 403 || !st) return { disponible: false, estado: null, error: null };
        return { disponible: false, estado: null, error: datosDelError(e).detail };
      }
    ),
  };
  return cache.promesa;
}

// `clave` = clínica + usuario de la sesión, o null para NO consultar (p. ej. el modal en modo
// «cobro nuevo», la demo, o sin sesión): entonces no hay petición y no hay nada que pintar.
export function useEstadoCfdi(clave) {
  const [res, setRes] = useState({ cargando: clave != null, disponible: false, estado: null, error: null });
  useEffect(() => {
    if (clave == null) {
      setRes({ cargando: false, disponible: false, estado: null, error: null });
      return undefined;
    }
    let vivo = true;
    cargarEstadoCfdi(clave).then((r) => { if (vivo) setRes({ cargando: false, ...r }); });
    return () => { vivo = false; };
  }, [clave]);
  const recargar = async ({ exigirFacturapi = false } = {}) => {
    if (clave == null) return { disponible: false };
    // Sin la caché del panel: cada GET relee la organización en el backend.
    invalidarEstadoCfdi();
    const r = await cargarEstadoCfdi(clave);
    // Al revisar la firma, una lectura fallida conserva el panel para poder reintentar.
    // No se interpreta facturapi: null (proveedor caído) como ausencia de pendientes.
    if (exigirFacturapi && !Array.isArray(r.estado?.facturapi?.pending_steps)) {
      invalidarEstadoCfdi();
      const e = new Error();
      e.response = { data: { detail: r.error } };
      throw e;
    }
    setRes({ cargando: false, ...r });
    return r;
  };
  return { ...res, recargar };
}

// Clave de la consulta: la clínica activa y el usuario (otra sesión = otra respuesta).
// 🔴 Sin `user.cfdi_pacientes === true` (lo pone /auth/me SÓLO con la bandera encendida para la
//    clínica) no se hace NINGUNA petición: apagada, el panel no pide ni recibe un 404 de más.
export function claveCfdi(activeClinic, user, activo = true) {
  if (!activo || !user || user.is_demo_session || user.cfdi_pacientes !== true) return null;
  const cid = activeClinic?.id || user.active_clinic_id;
  return cid ? `${cid}:${user.id}` : null;
}

export async function descargarCfdi(id, tipo, uuid) {
  const res = await api.get(`/cfdis/${encodeURIComponent(id)}/${tipo}`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `factura-${(uuid || id).slice(0, 8)}.${tipo}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const CLASE_ESTADO = {
  vigente: "bg-emerald-50 text-emerald-700",
  timbrando: "bg-amber-50 text-amber-700",
  reservado: "bg-amber-50 text-amber-700",
  cancelacion_solicitada: "bg-amber-50 text-amber-700",
  cancelado: "bg-slate-100 text-slate-600",
  error: "bg-red-50 text-red-700",
};
